import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import LazyLoad from 'react-lazyload';
import { allListings } from '../data/listings';
import { generateStructuredData, generatePageTitle, generateMetaDescription, generateBreadcrumbSchema } from '../utils/seoUtils';
import WhatsAppIcon from '../components/WhatsAppIcon';

const CenterDetailPage = () => {
    const { id } = useParams();
    const listing = allListings.find(l => l.id === id);

    if (!listing) {
        return (
            <div className="container mx-auto px-6 py-32 text-center">
                <div className="text-6xl mb-6">🏜️</div>
                <h1 className="text-3xl font-bold text-slate-900 mb-4">Centro no encontrado</h1>
                <p className="text-slate-500 mb-8">Lo sentimos, no pudimos encontrar el centro que estás buscando.</p>
                <Link to="/" className="btn-premium-secondary inline-block">Volver al inicio</Link>
            </div>
        );
    }

    const breadcrumbs = [
        { name: 'Inicio', url: '/' },
        { name: 'Centros', url: '/' },
        { name: listing.name, url: `/centro/${listing.id}` }
    ];

    return (
        <>
            <Helmet>
                <title>{generatePageTitle.center(listing)}</title>
                <meta name="description" content={generateMetaDescription.center(listing)} />
                <link rel="canonical" href={`https://esteticasdeargentina.com/centro/${listing.id}`} />
                <script type="application/ld+json">
                    {JSON.stringify(generateStructuredData.centerDetail(listing))}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(generateBreadcrumbSchema(breadcrumbs))}
                </script>
            </Helmet>

            <article className="pb-32" itemScope itemType="https://schema.org/MedicalBusiness">
                {/* Header / Hero */}
                <header className="relative bg-slate-900 pt-32 pb-64 overflow-hidden">
                    <div className="absolute inset-0 opacity-60">
                        <img
                            src={listing.images?.[0] || 'https://images.unsplash.com/photo-1519415510236-8a5ad642ad39?auto=format&fit=crop&w=1600&q=80'}
                            alt={listing.name}
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-slate-900/80 to-slate-900" />
                    </div>

                    <div className="container mx-auto px-6 relative z-10 text-center">
                        <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full text-xs font-bold text-white uppercase tracking-widest mb-8">
                            <span>{listing.listingType}</span>
                            {listing.isFeatured && <span className="text-amber-400">★ Destacado</span>}
                        </div>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight" itemProp="name">
                            {listing.name}
                        </h1>

                        <div className="flex flex-wrap justify-center items-center gap-6 text-slate-300">
                            <div className="flex items-center">
                                <svg className="w-5 h-5 mr-2 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                </svg>
                                <span>{listing.city}, {listing.province}</span>
                            </div>
                            <div className="flex items-center" itemProp="aggregateRating" itemScope itemType="https://schema.org/AggregateRating">
                                <div className="flex text-amber-400 mr-2">
                                    {[...Array(5)].map((_, i) => (
                                        <svg key={i} className={`w-4 h-4 ${i < Math.floor(listing.rating) ? 'fill-current' : 'text-slate-600'}`} viewBox="0 0 20 20">
                                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                        </svg>
                                    ))}
                                </div>
                                <span className="font-bold text-white"><span itemProp="ratingValue">{listing.rating}</span>/5</span>
                                <meta itemProp="ratingCount" content="50" />
                            </div>
                        </div>
                    </div>
                </header>

                <div className="container mx-auto px-6 -mt-32 relative z-20">
                    <div className="grid lg:grid-cols-3 gap-12">
                        {/* Main Content */}
                        <div className="lg:col-span-2 space-y-12">
                            {/* Stats Cards */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                <div className="glass-card p-6 text-center">
                                    <div className="text-rose-500 font-bold text-xl mb-1">{listing.experience}</div>
                                    <div className="text-[10px] uppercase tracking-widest text-slate-600 font-bold">Experiencia</div>
                                </div>
                                <div className="glass-card p-6 text-center">
                                    <div className="text-violet-500 font-bold text-xl mb-1">{listing.tier === 1 ? 'Expert' : 'Certified'}</div>
                                    <div className="text-[10px] uppercase tracking-widest text-slate-600 font-bold">Respaldo</div>
                                </div>
                                <div className="glass-card p-6 text-center">
                                    <div className="text-rose-500 font-bold text-xl mb-1">Verificado</div>
                                    <div className="text-[10px] uppercase tracking-widest text-slate-600 font-bold">Estado</div>
                                </div>
                                <div className="glass-card p-6 text-center">
                                    <div className="text-violet-500 font-bold text-xl mb-1">{listing.specialties?.length}</div>
                                    <div className="text-[10px] uppercase tracking-widest text-slate-600 font-bold">Servicios</div>
                                </div>
                            </div>

                            <section className="space-y-6">
                                <h2 className="text-3xl font-bold text-slate-900 border-l-4 border-rose-500 pl-6">Sobre el centro</h2>
                                <p className="text-lg text-slate-600 leading-relaxed" itemProp="description">
                                    {listing.description}
                                </p>
                            </section>

                            <section className="space-y-6">
                                <h2 className="text-2xl font-bold text-slate-900">Especialidades Destacadas</h2>
                                <div className="flex flex-wrap gap-3">
                                    {listing.specialties?.map((s, i) => (
                                        <div key={i} className="bg-white px-6 py-3 rounded-2xl border border-slate-100 shadow-sm font-medium text-slate-700">
                                            {s}
                                        </div>
                                    ))}
                                </div>
                            </section>

                            {listing.images && listing.images.length > 0 && (
                                <section className="space-y-6">
                                    <h2 className="text-2xl font-bold text-slate-900">Galería de Instalaciones</h2>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {listing.images.map((img, i) => (
                                            <div key={i} className="rounded-3xl overflow-hidden aspect-video">
                                                <LazyLoad height={300} offset={200}>
                                                    <img src={img} alt={`${listing.name} ${i + 1}`} className="w-full h-full object-cover" />
                                                </LazyLoad>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}
                        </div>

                        {/* Sidebar */}
                        <aside className="space-y-8">
                            <div className="glass-card p-8 sticky top-28 border-rose-100">
                                <h3 className="text-xl font-bold text-slate-900 mb-6">Información de Contacto</h3>

                                <div className="space-y-6 mb-8 text-sm">
                                    <div className="flex items-start">
                                        <div className="w-8 h-8 rounded-full bg-rose-50 flex items-center justify-center mr-4 shrink-0">
                                            <svg className="w-4 h-4 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                            </svg>
                                        </div>
                                        <span className="text-slate-600 pt-1 leading-relaxed">
                                            {listing.contact?.address || 'Dirección por confirmar'}<br />
                                            <strong>{listing.city}, {listing.province}</strong>
                                        </span>
                                    </div>

                                    <div className="flex items-start">
                                        <div className="w-8 h-8 rounded-full bg-violet-50 flex items-center justify-center mr-4 shrink-0">
                                            <svg className="w-4 h-4 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <span className="text-slate-600 pt-1 leading-relaxed">
                                            {listing.hours}
                                        </span>
                                    </div>

                                    {listing.contact?.phone && (
                                        <div className="flex items-start">
                                            <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center mr-4 shrink-0">
                                                <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                                </svg>
                                            </div>
                                            <a href={`tel:${listing.contact.phone}`} className="text-slate-600 pt-1 hover:text-rose-600 transition-colors">
                                                {listing.contact.phone}
                                            </a>
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-4">
                                    {listing.calendarLink && (
                                        <a href={listing.calendarLink} target="_blank" rel="noopener noreferrer" className="btn-premium-primary w-full">
                                            <span>Reservar cita</span>
                                        </a>
                                    )}
                                    {listing.contact?.whatsapp && (
                                        <a href={listing.contact.whatsapp} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-full py-3 px-6 rounded-xl font-semibold bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-all gap-2">
                                            <WhatsAppIcon className="w-5 h-5" />
                                            WhatsApp
                                        </a>
                                    )}
                                    {listing.contact?.website && (
                                        <a href={listing.contact.website} target="_blank" rel="noopener noreferrer" className="btn-premium-secondary w-full text-center">
                                            Sitio Web
                                        </a>
                                    )}
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>
            </article>
        </>
    );
};

export default CenterDetailPage;